import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sprout, Award, Users, Globe } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Card, Lead, SideImage } from '../components/common/Primitives';
import banner from '../assets/banners/about.jpg';
import growingLanes from '../assets/farm/growing-lanes.jpg';
import harvestCrate from '../assets/farm/harvest-crate.jpg';

const values = [
  { Icon: Sprout, title: 'Integrity', body: 'We grow what we promise: no shortcuts and no hidden practices.' },
  { Icon: Award, title: 'Excellence', body: 'We keep innovating to deliver the finest produce we can.' },
  { Icon: Users, title: 'Community', body: 'Our members are family. We grow for them, and with them.' },
  { Icon: Globe, title: 'Sustainability', body: 'Every decision prioritises the health of the planet.' },
];

const differences = [
  ['100% pesticide-free', 'No chemicals, no compromises. Clean, nutrient-dense produce grown in a controlled environment.'],
  ['Harvested and delivered same day', 'From greenhouse to table in under six hours.'],
  ['Limited to 500 families', 'Exclusivity is what protects quality. We grow for members, not for markets.'],
  ['90% less water', 'Hydroponic and aeroponic systems use a fraction of what traditional farming requires.'],
  ['Full transparency', 'Every member is invited to visit. See where and how your food grows.'],
  ['Community, not commerce', 'We are building relationships rather than transactions.'],
];

const About = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="About"
      title="About Annpriya"
      lead="We are not just a farm. We are a collective of growers, scientists and engineers who believe food should be grown with integrity, eaten with joy, and delivered with transparency."
    />

    {/* The story keeps its own reading measure rather than stretching to meet
        the picture. Both tracks are capped and pushed to opposite edges, so the
        slack on a wide screen falls between them instead of leaving the image
        stranded in the middle of the page. */}
    <Section tone="paper">
      <div className="grid lg:grid-cols-[minmax(0,48rem)_minmax(0,22rem)] lg:justify-between gap-10 lg:gap-12 items-center">
        <div>
          <Head eyebrow="Our story" title="Restoring the soul of food" />
          <div className="mt-8 space-y-5 text-soil/75 text-[16px] leading-relaxed">
            <p>
              Food shapes lives. It defines health, community and culture. But somewhere along the
              way food became a commodity: stored for weeks, shipped across continents, coated in
              chemicals and stripped of its character.
            </p>
            <p className="font-display font-semibold text-[1.6rem] text-ink leading-snug py-2">
              Annpriya was created to restore that.
            </p>
            <p>
              We grow a limited harvest for a limited number of families, under conditions we
              measure continuously and can account for afterwards. Nothing is sprayed. Nothing
              sits in transit. Nothing is guessed at.
            </p>
          </div>
        </div>
        <SideImage
          src={harvestCrate}
          alt="A grower carrying a crate of just-picked vegetables"
        />
      </div>
    </Section>

    <Section tone="paper2">
      <Head eyebrow="Different" title="What sets it apart" center />
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {differences.map(([title, body]) => (
          <Card key={title} className="p-7">
            <h3 className="font-display font-semibold text-[1.15rem] text-ink">{title}</h3>
            <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{body}</p>
          </Card>
        ))}
      </div>
    </Section>

    <Section tone="paper">
      <Head eyebrow="Our values" title="What we stand for" center />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {values.map(({ Icon, title, body }) => (
          <div key={title} className="text-center">
            <span className="w-16 h-16 rounded-2xl grid place-items-center bg-forest/10 text-forest mx-auto">
              <Icon className="w-7 h-7" />
            </span>
            <h3 className="mt-5 font-display font-semibold text-[1.2rem] text-ink">{title}</h3>
            <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section tone="soil">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <SideImage
          tone="dark"
          src={growingLanes}
          alt="An Annpriya agronomist harvesting in the greenhouse growing lanes"
        />
        <div>
          <Head
            tone="dark"
            eyebrow="Our farm"
            title="The Annpriya greenhouse"
            lead="Located in Jaipur, Rajasthan, our greenhouse spans acres of controlled growing environment."
          />
          <div className="mt-7 space-y-4 text-paper/65 text-[15px] leading-relaxed">
            <p>
              Using hydroponics and aeroponics we grow more than 50 varieties of greens, herbs and
              vegetables, without soil, without pesticides and without compromise.
            </p>
            <p>
              Every member is welcome to visit. Walk the growing lanes, meet the agronomists, and
              see first-hand how your food is grown.
            </p>
          </div>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/experiences"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-ink px-6 py-3 font-semibold hover:bg-paper transition-colors"
            >
              Book a farm visit <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/farm-tech"
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-white/20 px-6 py-3 font-medium text-paper hover:ring-lime hover:text-lime transition-colors"
            >
              How we grow
            </Link>
          </div>
        </div>
      </div>
    </Section>

    <Section tone="ink">
      <div className="max-w-3xl mx-auto text-center">
        <Head
          tone="dark"
          eyebrow="Our vision"
          title="The future we are growing"
          center
        />
        <Lead tone="dark" className="mt-7">
          A world where every family has access to food that is pure, fresh and traceable. Where
          agriculture coexists with nature rather than against it. Where knowing your farmer is not
          a luxury but a right.
        </Lead>
        <Link
          to="/membership"
          className="inline-flex items-center gap-2 mt-10 rounded-full bg-lime text-ink px-7 py-3.5 font-semibold hover:bg-paper transition-colors"
        >
          Request membership <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  </div>
);

export default About;
